import { type CheckoutFormValues } from '@cart-services/zod-schema/checkoutFormSchema'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { usePrevious } from '@dni-react-hooks/use-previous'
import type * as TapPay from '@dni-types/payment/tappay'
import { TapPayUpdateStatus } from '@dni-types/payment/tappay'
import { CARD_SOURCE, TappayCreditCardModule, useCkoFlowCardValidation } from '@payments'
import { BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { isEmail } from '@utils'
import { validatePhoneNumber } from '@utils/validate-phone-number'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useContextPayment } from '../components/PaymentProvider/PaymentProvider'
import { type AppliedPaymentModule } from '../utils/utility-process-pay-module'

export const useHandleValidateCreditCard = (
  paymentModule: AppliedPaymentModule | undefined,
  appliedPaymentMethodId: number | null,
  isSdkInitialized: boolean,
  /** STM 等 inline CKO 場景傳 false，覆蓋 mountsInDialog() 的 module-level 預設值 */
  isCreditCardInDialog?: boolean,
) => {
  const { clearErrors, setError } = useFormContext<CheckoutFormValues>()
  const { cardSource, userPaymentTokenInfo, creditCardholderInfo, creditCardUpdateInfo } = useContextPayment()
  const cardValidateDataRef = useRef<{
    tappay: TapPay.CardUpdate | null
  }>({
    tappay: null,
  })
  /** 卡片欄位在彈窗內才驗證，送出前不重複檢查。 */
  const isDialogFlowModule = isCreditCardInDialog !== undefined
    ? isCreditCardInDialog
    : paymentModule instanceof BaseCheckoutPayFlowModule && paymentModule.mountsInDialog()
  const { validate: validateCkoFlowCard } = useCkoFlowCardValidation({
    paymentModule: isDialogFlowModule ? undefined : paymentModule,
    isSdkInitialized,
    onValidityChange: () => clearErrors('card.creditCardNumber'),
  })
  const validateCreditCard = () => {
    /** 如有 userPaymentTokenInfo，則跳過驗證信用卡 */
    if (userPaymentTokenInfo && cardSource === CARD_SOURCE.REMEMBERED) return true
    if (paymentModule instanceof TappayCreditCardModule) {
      if (!cardValidateDataRef.current.tappay?.canGetPrime) return false
    }
    return validateCkoFlowCard()
  }

  const handleCreditCardholderUpdateErrors = useCallbackEvent(() => {
    if (!creditCardUpdateInfo) {
      return
    }
    if (creditCardUpdateInfo.status.name_en === TapPayUpdateStatus.FAILURE
      || creditCardUpdateInfo.status.name_en === TapPayUpdateStatus.UNKNOWN
    ) {
      setError('card.cardholderName', { message: '請輸入正確的持卡人姓名' }, { shouldFocus: true })
      throw new Error('Validation: cardholder name failed.')
    }
    if (creditCardUpdateInfo.status.email === TapPayUpdateStatus.UNKNOWN
      && (creditCardUpdateInfo.status.phone_country_code === TapPayUpdateStatus.UNKNOWN
        || creditCardUpdateInfo.status.phone_number === TapPayUpdateStatus.UNKNOWN)
    ) {
      setError('card.cardholderEmail', { message: 'Email 或手機號碼須擇一填寫' }, { shouldFocus: true })
      setError('card.cardholderPhoneNumber', { message: 'Email 或手機號碼須擇一填寫' }, { shouldFocus: true })
      throw new Error('Validation: no valid phone or email.')
    }
    if (creditCardUpdateInfo.status.email === TapPayUpdateStatus.FAILURE) {
      setError('card.cardholderEmail', { message: '請輸入正確的 Email' }, { shouldFocus: true })
      throw new Error('Validation: email failed.')
    }
    if (creditCardUpdateInfo.status.phone_country_code === TapPayUpdateStatus.FAILURE
      || creditCardUpdateInfo.status.phone_number === TapPayUpdateStatus.FAILURE) {
      setError('card.cardholderPhoneNumber', { message: '請輸入正確的手機號碼' }, { shouldFocus: true })
      throw new Error('Validation: phone failed.')
    }
  })
  /** For cardholder info maintained by us */
  const handleCreditCardholderCompleteUpdateErrors = useCallbackEvent(() => {
    if (!creditCardholderInfo) {
      return
    }
    const { name_en, email, phone_number, phone_number_country_code } = creditCardholderInfo
    if (!name_en) {
      setError('card.cardholderInfoComplete.cardholderName', { message: '必填欄位！' }, { shouldFocus: true })
      throw new Error('Validation: cardholder name failed.')
    }
    if (!email && (!phone_number_country_code || !phone_number)) {
      setError('card.cardholderInfoComplete.cardholderEmail', { message: 'Email 或手機號碼須擇一填寫' }, { shouldFocus: true })
      setError('card.cardholderInfoComplete.cardholderPhone', { message: 'Email 或手機號碼須擇一填寫' }, { shouldFocus: true })
      throw new Error('Validation: no valid phone or email.')
    }
    const isValidEmail = isEmail(email.trim())
    const isValidPhone = validatePhoneNumber(phone_number, phone_number_country_code)
    if (!isValidEmail && !isValidPhone) {
      if (!isValidEmail) {
        setError('card.cardholderInfoComplete.cardholderEmail', { message: '請輸入正確的 Email' }, { shouldFocus: true })
        throw new Error('Validation: email failed.')
      }
      if (!isValidPhone) {
        setError('card.cardholderInfoComplete.cardholderPhone', { message: '請輸入正確的 Email' }, { shouldFocus: true })
        throw new Error('Validation: phone failed.')
      }
    }
  })

  const handleValidateCreditCard = useCallbackEvent(() => {
    clearErrors([
      'card.cardholderName',
      'card.cardholderEmail',
      'card.cardholderPhoneNumber',
      'card.creditCardNumber',
      'card.cardholderInfoComplete.cardholderName',
      'card.cardholderInfoComplete.cardholderEmail',
      'card.cardholderInfoComplete.cardholderPhone',
    ])
    /** Only Tappay needs to handle cardholder info */
    if ((paymentModule instanceof TappayCreditCardModule)) {
      if (cardSource === CARD_SOURCE.REMEMBERED) {
        handleCreditCardholderCompleteUpdateErrors()
      }
      else {
      /** Handle Tappay verification result */
        handleCreditCardholderUpdateErrors()
      }
    }
    const isCreditCardValid = validateCreditCard()
    if (!isCreditCardValid) {
      setError('card.creditCardNumber', { message: '請輸入正確的信用卡資訊' }, { shouldFocus: true })
      throw new Error('Validate credit card number failed.')
    }
  })

  useEffect(() => {
    // NOTE: 需等待 SDK 初始化完畢，才能監聽 cardUpdate 事件
    if (!isSdkInitialized) return
    if (!(paymentModule instanceof TappayCreditCardModule)) {
      return
    }
    const eventHandler = (data: TapPay.CardUpdate) => {
      clearErrors('card.creditCardNumber')
      cardValidateDataRef.current.tappay = data
    }
    paymentModule.addEventListener('cardUpdate', eventHandler)
    return () => {
      paymentModule.removeEventListener('cardUpdate', eventHandler)
    }
  }, [clearErrors, isSdkInitialized, paymentModule])

  /**
   * 切換付款方式時，清除信用卡錯誤
   */
  const prevAppliedPaymentMethodId = usePrevious(appliedPaymentMethodId)
  useEffect(() => {
    if (prevAppliedPaymentMethodId !== appliedPaymentMethodId) {
      clearErrors('card.creditCardNumber')
    }
  }, [appliedPaymentMethodId, clearErrors, prevAppliedPaymentMethodId])

  return {
    handleValidateCreditCard,
  }
}
